import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, usePresence } from "framer-motion";
import './contact.css';
import Showreel from "../../components/Showreel/Showreel";
import { defaultInfoVariants, horizGridVariants, pageVariants, rightImgVariants, tickerSectionVariants, vertGridVariants } from "./variants";
import { emojiBlast } from "emoji-blast";
import debounce from "lodash.debounce";
import { contactData } from "../../globals/data";
import { standardItemVariants } from "../ContactMob/variants";
import { primaryInput } from "detect-it";


const Contact = () => {
    const mouseTrackerWrapperRef = useRef(null);
    const isShowreelVisibleRef = useRef(false);
    const [isGhostHovering, setIsGhostHovering] = useState(false);
    const isGhostHoveringRef = useRef(false);
    const [isGhostActive, setIsGhostActive] = useState(false);
    const isGhostActiveRef = useRef(false);
    const ghostFirstDelay = 500;
    const pageStartTime = performance.now();

    const [imageCounter, setImageCounter] = useState(1);
    const isPresentRef = useRef(true);
    const [isPresent, safeToRemove] = usePresence();
    const [isColour, setIsColour] = useState(true);
    const [isShowreelVisible, setIsShowreelVisible] = useState(false);
    const [pointersActive, setPointersActive] = useState(false);
    const [timeoutEmoji, setTimeoutEmoji] = useState(false);
    const btsImageCountRefs = useRef([]);
    const [remountTicker, setRemountTicker] = useState(false);
    const [tickerFontSize, setTickerFontSize] = useState(null);
    const clCoverRef = useRef(null);
    const crCoverRef = useRef(null);

    // Define variables for the ghost video cursor
    const textGhostRefs = useRef([]);
    const ghostRefs = useRef([]);
    const ghostTextOpacities = [0.1, 0.2, 0.3, 0.4, 0.5];
    const ghostEasings = [0.7, 0.85, 0.95, 1.1, 1.2]
    const ghostOpacities = [0.1, 0.2, 0.3, 0.6, 0.8];
    const ghostZs = [6, 5, 4, 3, 2, 1]
    const ghostBlur = [.2, .3, .4, .5, .6]
    const numGhosts = 5;
    const LERP_FACTOR = 0.05;

    const imgWrapperRef = useRef();
    const personalRefs = useRef([]);
    const btsLength = contactData.numberStills;

    const targetX = useRef(window.innerWidth/2);
    const currentX = useRef(Array(numGhosts).fill(window.innerWidth/2));

    const targetY = useRef(window.innerHeight/2);
    const currentY = useRef(Array(numGhosts).fill(window.innerHeight/2));

    const handleClick = (e) => {
        if(isGhostActiveRef.current && !isShowreelVisible){
            setIsGhostActive(false);
            isGhostActiveRef.current = false;
            setIsShowreelVisible(true);
            isShowreelVisibleRef.current = true;
        }
    }

    const handleMouseMove = (e) => {
        let pointerX = e.clientX;
        let pointerY = e.clientY;

        if(pointerY <= 40 || !isPresentRef.current){
            setIsGhostActive(false);
            isGhostActiveRef.current = false;
        } else if(!isGhostActiveRef.current && (performance.now() - pageStartTime) > ghostFirstDelay && !isGhostHoveringRef.current && !isShowreelVisibleRef.current){
            setIsGhostActive(true);
            isGhostActiveRef.current = true;
        }

        // On mouse move, set the target X and targetY to reflect current position of mouse
        targetX.current = pointerX;
        targetY.current = pointerY;
    }

    const animate = () => {
        if(targetX.current && targetY.current){
            currentX.current.forEach((item, idx) => {
                // For each currentX we're tracking for each video ghost, we want to update it by getting the 
                // delta between the target (current mouse position) and the position of the current ghost, then
                // multiplying this by the LERP_factor and the scaler for this specific ghost defined by ghostEasings.
                let deltaX = (targetX.current - item) * (LERP_FACTOR * ghostEasings[idx])
                let deltaY = (targetY.current - currentY.current[idx]) * (LERP_FACTOR * ghostEasings[idx]);

                // Calculate the speed and then the skew of the element
                let speedX = deltaX/window.innerWidth;
                let skewX = speedX*750;

                let speedY = deltaY/window.innerHeight;
                let skewY = speedY*750;

                // if our deltas are large enough update the currentX value
                if(Math.abs(deltaX) > 0.1){
                    currentX.current[idx] = currentX.current[idx] + deltaX;
                }

                if(Math.abs(deltaY) > 0.1){
                    currentY.current[idx] = currentY.current[idx] + deltaY;
                }

                //Update the videos & text positions
                ghostRefs.current[idx].style.transform = `translate3d(${currentX.current[idx]}px, ${currentY.current[idx]}px, 0) skew(${skewX}deg, ${skewY}deg)`
                textGhostRefs.current[idx].style.transform = `translate3d(calc(${currentX.current[idx]-0.1}px - 50%), calc(${currentY.current[idx]-0.1}px - 50%), 0) skew(${skewX}deg, ${skewY}deg)`
            });
        }
        if(isPresentRef.current)requestAnimationFrame(animate)
    }

    const init = () => {
        requestAnimationFrame(animate)
    }

    const resizeListener = () => {
        let tickerHeight = window.innerHeight*0.25;
        let tickerWidth = window.innerWidth*0.7;
        let minNumLines = 9;
        let fontSizeWidth = tickerWidth/45;
        let fontSizeHeight = tickerHeight/minNumLines;
        setTickerFontSize(Math.max(fontSizeWidth, fontSizeHeight));
    }

    useEffect(() => {
        resizeListener()
        window.addEventListener('resize', resizeListener)

        init()
        window.addEventListener('mousemove', handleMouseMove)
        if(primaryInput == 'mouse') window.addEventListener('click', handleClick)

        setTimeout(() => {
            setPointersActive(true);
            debounceAutomaticIncrement(1, 1)
        }, 1000)

        setTimeout(() => {
            try{clCoverRef.current.style.opacity = 1}catch{}
            try{crCoverRef.current.style.opacity = 1}catch{}
        }, 650)

        // remount the marquee ticker
        let a = setInterval(() => {
            setRemountTicker(true);
            setTimeout(() => {
                setRemountTicker(false)
            }, 1100)
        }, 60000)
       
        return () => {
            try{
                clearInterval(a);
                window.removeEventListener('resize', resizeListener);
                window.removeEventListener('mousemove', handleMouseMove);
                if(primaryInput == 'mouse') window.removeEventListener('click', handleClick);
            }catch{}
        }
    }, [])

    const handleShowreelClick = (is) => {
        setIsShowreelVisible(!isShowreelVisible);
        isShowreelVisibleRef.current = !isShowreelVisibleRef.current;
    }

    const handleRatingClick = (isGood) => {
        if(isGood){
            emojiBlast({
                emojis: ["🔥", "😍", "😘", "🥰", "❤️"],
            })
        } else{
            emojiBlast({
                emojis: ["😢", "🤬", "😢", "👺", "😭"],
            })
        }
    }

    useEffect(() => {
        if(!isPresent){
            try{clCoverRef.current.style.opacity = 0}catch{}
            try{crCoverRef.current.style.opacity = 0}catch{}
            safeToRemove();
        }
        isPresentRef.current = isPresent;
    }, [isPresent])

    useEffect(() => {
        if(timeoutEmoji){
            setTimeout(() => {
                setTimeoutEmoji(false)
            }, 350)
        }
    }, [timeoutEmoji])

    // Handles changes to the BTS images
    const handleImageChange = (isIncrement) => {
        
        //exit image counter
        try{
            btsImageCountRefs.current[imageCounter-1].classList.add("bts-anim");
            if(isIncrement > 0){
                btsImageCountRefs.current[imageCounter-1].classList.add("next-exit-counter")
                btsImageCountRefs.current[imageCounter-1].classList.remove("active-counter")
            } else{
                btsImageCountRefs.current[imageCounter-1].classList.add("prev-exit-counter")
                btsImageCountRefs.current[imageCounter-1].classList.remove("active-counter")
            }
        } catch{

        }

        let newVal = imageCounter + isIncrement;

        debounceAutomaticIncrement.cancel();
        debounceAutomaticIncrement(newVal, isIncrement);
        if(newVal <= 0){
            // to end
            newVal = (btsLength)
            for(let i = 0; i<btsLength-1; i++){
                if(i !== (imageCounter -1)){
                    btsImageCountRefs.current[i].classList.remove("prev-exit-counter")
                    btsImageCountRefs.current[i].classList.remove("bts-anim")
                    btsImageCountRefs.current[i].classList.add("next-exit-counter")
                }
            }
        }
        else if(newVal > btsLength){
            //back to start
            newVal = 1;
            for(let i = 0; i<btsLength-1; i++){
                if(i !== (imageCounter -1)){
                    btsImageCountRefs.current[i].classList.remove("next-exit-counter")
                    btsImageCountRefs.current[i].classList.remove("bts-anim")
                    btsImageCountRefs.current[i].classList.add("prev-exit-counter")
                }
            }
        }

        btsImageCountRefs.current[newVal-1].classList.add("bts-anim")
        btsImageCountRefs.current[newVal-1].classList.remove("next-exit-counter")
        btsImageCountRefs.current[newVal-1].classList.remove("prev-exit-counter")
        btsImageCountRefs.current[newVal-1].classList.add("active-counter")
        setImageCounter(newVal);

        // create image
        try{
            let newEle = document.createElement("div");
            let bgEle = document.createElement("div");
            bgEle.classList.add("bts-img");
            newEle.classList.add("bts-img-wrap");
            newEle.appendChild(bgEle);
            bgEle.style.backgroundImage = 'url("../media/BTS/placeholder/BTS_'+(newVal-1)+'.webp")';

            imgWrapperRef.current.appendChild(newEle);
        } catch{}

        

        let currentImages = document.getElementsByClassName('bts-img-wrap');
        if(currentImages.length > 20){
            for(let i=10; i>0; i--){
                currentImages[i].remove();
            }
        }
    }

    const debounceAutomaticIncrement = useCallback(
        debounce((useVal, dir) => {
            try{
                let isIncrement = dir;
                try{
                    btsImageCountRefs.current[useVal-1].classList.add("bts-anim");
                    if(isIncrement > 0){
                        btsImageCountRefs.current[useVal-1].classList.add("next-exit-counter")
                        btsImageCountRefs.current[useVal-1].classList.remove("active-counter")
                    } else{
                        btsImageCountRefs.current[useVal-1].classList.add("prev-exit-counter")
                        btsImageCountRefs.current[useVal-1].classList.remove("active-counter")
                    }
                } catch{
        
                }
        
                let newVal = useVal + isIncrement;
                if(newVal <= 0){
                    // to end
                    newVal = (btsLength)
                    for(let i = 0; i<btsLength-1; i++){
                        if(i !== (useVal -1)){
                            btsImageCountRefs.current[i].classList.remove("prev-exit-counter")
                            btsImageCountRefs.current[i].classList.remove("bts-anim")
                            btsImageCountRefs.current[i].classList.add("next-exit-counter")
                        }
                    }
                }
                else if(newVal > btsLength){
                    //back to start
                    newVal = 1;
                    for(let i = 0; i<btsLength-1; i++){
                        if(i !== (useVal -1)){
                            btsImageCountRefs.current[i].classList.remove("next-exit-counter")
                            btsImageCountRefs.current[i].classList.remove("bts-anim")
                            btsImageCountRefs.current[i].classList.add("prev-exit-counter")
                        }
                    }
                }
                

                try{
                btsImageCountRefs.current[newVal-1].classList.add("bts-anim")
                btsImageCountRefs.current[newVal-1].classList.remove("next-exit-counter")
                btsImageCountRefs.current[newVal-1].classList.remove("prev-exit-counter")
                btsImageCountRefs.current[newVal-1].classList.add("active-counter")
                setImageCounter(newVal);
                } catch{}
        
                // create image
                try{
                    let newEle = document.createElement("div");
                    let bgEle = document.createElement("div");
                    bgEle.classList.add("bts-img");
                    newEle.classList.add("bts-img-wrap");
                    newEle.appendChild(bgEle);
                    bgEle.style.backgroundImage = 'url("../media/BTS/placeholder/BTS_'+(newVal-1)+'.webp")';
        
                    imgWrapperRef.current.appendChild(newEle);
                } catch{}
                debounceAutomaticIncrement(newVal, isIncrement);
            }catch{}

        }, 5000),
        []
    );

    useEffect(() => {
        if(isGhostActive) document.body.style.cursor = 'pointer'
        else document.body.style.cursor = 'normal';
    }, [isGhostActive])

    const handleImageCounter = (isIncrement) => {
        handleImageChange(isIncrement)
    }

    const hideGhost = () => {
        debounceShowGhost.cancel()
        setIsGhostHovering(true);
        isGhostHoveringRef.current = true;
        setIsGhostActive(false);
        isGhostActiveRef.current = false;
    }

    const showGhost = () => {
        debounceShowGhost.cancel();
        debounceShowGhost();
    }

    const debounceShowGhost = useCallback(
        debounce(() => {
            setIsGhostActive(true);
            isGhostActiveRef.current = true;
            setIsGhostHovering(false);
            isGhostHoveringRef.current = false
          }, 200),
        []
    );

    // Debouncing implemented to prevent the video ghost from popping in/out instantly if the
    // user is mvoving quickly between interactable elements
    const socialMouseOut = () => {
        debounceShowGhost.cancel();
        debounceShowGhost()
    }

    const socialMouseOver = (who) => {
        debounceShowGhost.cancel()
        hideGhost()

        let targetEmoji;
        if(!timeoutEmoji){
            if(who == 0){
                targetEmoji = "😍";
            } else if(who == 1){
                targetEmoji = "🥶";
            } else if( who == 2){
                targetEmoji = "🐐"
            } else if (who == 3){
                targetEmoji = "🤓"
            } else{
                targetEmoji = "🤯"
            }
            let emojiTarget = personalRefs.current[who];
            emojiBlast({
                emojis: [targetEmoji],
                emojiCount: 5,
                physics: {
                    fontSize: { max: 30, min: 20 },
                    gravity: 0.25,
                    opacityDecay: 50, 
                    initialVelocities: {
                    y: {
                        max: -10,
                        min: -20,
                    }, x: {
                        max: 5,
                        min: -5
                    }
                    },
                },
                position() {
                return {
                    x: emojiTarget.offsetLeft + emojiTarget.clientWidth / 2,
                    y: emojiTarget.offsetTop + emojiTarget.clientHeight / 2
                };
                }
            });
            setTimeoutEmoji(true)
        }
    }

    return(
        <>
            <motion.div
                className={`contact_page__wrapper ${!pointersActive ? 'no-pe' : ''} `}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
            >
                <div className={`contact_content__wrapper ${isShowreelVisible ? 'no-pe' : ''}`}>
                    <div className="contact-upper__wrapper">
                        <div className="left-divide cont-upper">
                            <div className="cont-tl-col">
                                <div className="cont-services__wrapper">
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={0}>CINEMATIC STORYTELLING</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={1}>DIRECTING</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={2}>PRODUCTION</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={3}>EDITING</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={4}>GRADING</motion.span>
                                    </div>
                                </div>
                                {
                                    primaryInput !== 'mouse' &&
                                <div className="cont-showreel__wrapper" ref={el => personalRefs.current[4] = el}>
                                    <span
                                        className="contact-upper"
                                        style={{position: "relative"}}
                                        onMouseEnter={() => socialMouseOver(4)}
                                        onMouseLeave={() => socialMouseOut()}
                                        onClick={handleShowreelClick}
                                    >   
                                        <div className="service_item showreel-wrap">
                                            <motion.span variants={defaultInfoVariants} custom={5}
                                            
                                            >VIEW SHOWREEL
                                                
                                            </motion.span>
                                            <div className="box-hov-indicator box-sm" onMouseEnter={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.add("cursor-active")
                                                            }
                                                            catch{

                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.remove("cursor-active")
                                                            }
                                                            catch{

                                                            }
                                                        }}> 
                                                <span className="bx-h bx-tl"></span>
                                                <span className="bx-h bx-tr"></span>
                                                <span className="bx-h bx-bl"></span>
                                                <span className="bx-h bx-br"></span>

                                                <span className="bx-v bx-tl"></span>
                                                <span className="bx-v bx-tr"></span>
                                                <span className="bx-v bx-bl"></span>
                                                <span className="bx-v bx-br"></span>
                                            </div>
                                        </div>
                                        {/* <div className="attention-seeker" /> */}
                                        
                                        
                                    </span>
                                </div>
                                }
                                <div className="ego__wrapper">
                                    <div className="service_item">
                                        <motion.span 
                                            variants={defaultInfoVariants} custom={5}>DESIGN & DEVELOPMENT BY 
                                        </motion.span>
                                        <a href="https://www.linkedin.com/in/danny-lines-4113ab15a/" target="_blank">
                                        <motion.span 
                                            onMouseEnter={() => socialMouseOver(3)}
                                            onMouseLeave={() => socialMouseOut()}
                                            ref={el => personalRefs.current[3] = el}
                                            className="ego-name contact-upper"
                                            variants={defaultInfoVariants} custom={5}
                                        >
                                            @DANNY.LINES
                                            <div className="box-hov-indicator box-sm" onMouseEnter={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.add("cursor-active")
                                                            }
                                                            catch{

                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.remove("cursor-active")
                                                            }
                                                            catch{

                                                            }
                                                        }}> 
                                                <span className="bx-h bx-tl"></span>
                                                <span className="bx-h bx-tr"></span>
                                                <span className="bx-h bx-bl"></span>
                                                <span className="bx-h bx-br"></span>

                                                <span className="bx-v bx-tl"></span>
                                                <span className="bx-v bx-tr"></span>
                                                <span className="bx-v bx-bl"></span>
                                                <span className="bx-v bx-br"></span>
                                            </div>
                                        </motion.span>
                                        </a>
                                    </div>
                                    
                                </div>
                            </div>
                            <div className="cont-tm-col"/>
                            <div className="cont-tr-col">
                                <div className="cont-loc__wrapper">
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={3}>TOTTENHAM</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={4}>LONDON, UNITED KINGDOM</motion.span>
                                    </div>
                                    <div className="service_item">
                                        <motion.span variants={defaultInfoVariants} custom={5}>51.5072° N, 0.1276° W</motion.span>
                                    </div>
                                </div>
                                <div className="cont-p-socials__wrapper">
                                    <a
                                        className="contact-upper"
                                        href="https://www.instagram.com/tom_kral/"
                                        target="_blank"
                                        onMouseEnter={() => {
                                            socialMouseOver(0)
                                            try{
                                                let test = document.getElementById('cursor-circle');
                                                test.classList.add("cursor-active")
                                            }
                                            catch{

                                            }
                                        }}
                                        
                                    
                                        onMouseLeave={(e) => {
                                            try{
                                                let test = document.getElementById('cursor-circle');
                                                test.classList.remove("cursor-active")
                                                socialMouseOut()
                                            }
                                            catch{

                                            }
                                        }}
                                        ref={el => personalRefs.current[0] = el}
                                    >   
                                        <div className="service_item">
                                            <motion.span variants={defaultInfoVariants} custom={7}>@TOM.KRAL</motion.span>
                                        </div>
                                    
                                        <div className="box-hov-indicator box-sm"
                                            onMouseEnter={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.add("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.remove("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                        > 
                                            <span className="bx-h bx-tl"></span>
                                            <span className="bx-h bx-tr"></span>
                                            <span className="bx-h bx-bl"></span>
                                            <span className="bx-h bx-br"></span>

                                            <span className="bx-v bx-tl"></span>
                                            <span className="bx-v bx-tr"></span>
                                            <span className="bx-v bx-bl"></span>
                                            <span className="bx-v bx-br"></span>
                                        </div>
                                        {/* <span className="underline-interact" /> */}
                                    </a>
                                    <a
                                        className="contact-upper"
                                        href="https://www.instagram.com/jackylaboy"
                                        target="_blank"
                                        onMouseEnter={() => {socialMouseOver(1)}}
                                        onMouseLeave={socialMouseOut}
                                        ref={el => personalRefs.current[1] = el}
                                    >
                                        <div className="service_item">
                                            <motion.span variants={defaultInfoVariants} custom={8}>@JACK.MILLWARD</motion.span>
                                        </div>
                                        {/* <span className="underline-interact" /> */}
                                        <div className="box-hov-indicator box-sm"
                                            onMouseEnter={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.add("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.remove("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                        > 
                                            <span className="bx-h bx-tl"></span>
                                            <span className="bx-h bx-tr"></span>
                                            <span className="bx-h bx-bl"></span>
                                            <span className="bx-h bx-br"></span>

                                            <span className="bx-v bx-tl"></span>
                                            <span className="bx-v bx-tr"></span>
                                            <span className="bx-v bx-bl"></span>
                                            <span className="bx-v bx-br"></span>
                                        </div>
                                    </a>
                                    <a
                                        className="contact-upper"
                                        href="https://www.instagram.com/cm1lly/"
                                        target="_blank"
                                        onMouseEnter={() => {socialMouseOver(2)}}
                                        onMouseLeave={() => {socialMouseOut()}}
                                        ref={el => personalRefs.current[2] = el}
                                    >
                                        <div className="service_item">
                                            <motion.span variants={defaultInfoVariants} custom={9}>@CHARLIE.MILLWARD</motion.span>
                                        </div>
                                        {/* <span className="underline-interact" />     */}
                                        <div className="box-hov-indicator box-sm"
                                            onMouseEnter={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.add("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.remove("cursor-active")
                                                }
                                                catch{

                                                }
                                            }}
                                        > 
                                            <span className="bx-h bx-tl"></span>
                                            <span className="bx-h bx-tr"></span>
                                            <span className="bx-h bx-bl"></span>
                                            <span className="bx-h bx-br"></span>

                                            <span className="bx-v bx-tl"></span>
                                            <span className="bx-v bx-tr"></span>
                                            <span className="bx-v bx-bl"></span>
                                            <span className="bx-v bx-br"></span>
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="right-divide">
                            <div className="img-control__wrapper">
                                <motion.div 
                                    className="con-img-num__wrapper"
                                    variants={defaultInfoVariants}
                                    custom={11}    
                                >
                                    
                                        {
                                            Array(btsLength).fill(" ").map((item, idx)=> (
                                                <span
                                                    key={'bts-count-'+idx}
                                                    className={`con-bts-num ${idx == 0 ? 'active-counter' : ''}`}
                                                    ref={el => btsImageCountRefs.current[idx] = el}
                                                >
                                                    {(idx+1) < 10 ? '00' + (idx+1).toString() : '0'+(idx+1).toString()}
                                                </span>
                                            ))
                                        }
                                        <span style={{opacity: 0}}>9999</span>
                                    <span className="maxbts"> / {btsLength < 10 ? '00'+ btsLength : "0" + btsLength}</span>
                                </motion.div>
                                <motion.div className="con-next"
                                    variants={defaultInfoVariants}
                                    custom={13}
                                >
                                    <div className="service_item"> 
                                            <motion.span 
                                                className="contact-upper"
                                                onClick={() => handleImageCounter(1)}
                                                variants={standardItemVariants}
                                                custom={12}    
                                            >
                                                    NEXT
                                                {/* <span className="underline-interact" /> */}
                                                <div className="box-hov-indicator box-sm"
                                                    onMouseEnter={(e) => {
                                                        try{
                                                            let test = document.getElementById('cursor-circle');
                                                            test.classList.add("cursor-active");
                                                            hideGhost()
                                                        }
                                                        catch{

                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        try{
                                                            let test = document.getElementById('cursor-circle');
                                                            test.classList.remove("cursor-active");
                                                            showGhost()
                                                        }
                                                        catch{

                                                        }
                                                    }}
                                                > 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                            </motion.span>
                                        </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                    <div className="contact-mid__wrapper">
                        <div className="left-divide cont-mid-content__wrapper">
                            {
                                window.innerWidth > 1000 ?
                                <>
                                    <div className="contact-statement__wrapper">
                                        {
                                            "READY WHEN YOU ARE.".split("").flat().map((char, idx) => (
                                                char == " " ? 
                                                    <div className="service_item">
                                                        <motion.span variants={defaultInfoVariants} custom={5}>&nbsp;</motion.span>
                                                    </div>
                                                : 
                                                <div className="service_item">
                                                    <motion.span variants={defaultInfoVariants} custom={5+ (idx*0.25)}>{char}</motion.span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className="mid-contact-options">
                                        {/* <a 
                                            className="contact-social"
                                            href="https://vimeo.com/donprod" 
                                            target="_blank" 
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={8}>VIMEO</motion.span>
                                            </div>
                                            <div className="box-hov-indicator" 
                                                onMouseEnter={(e) => {
                                                    try{
                                                        let test = document.getElementById('cursor-circle');
                                                        test.classList.add("cursor-active")
                                                        hideGhost()
                                                    }
                                                    catch{

                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    try{
                                                        let test = document.getElementById('cursor-circle');
                                                        test.classList.remove("cursor-active")
                                                        showGhost()
                                                    }
                                                    catch{

                                                    }
                                                }}
                                            > 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a> */}
                                        <a 
                                            className="contact-social"
                                            href="https://www.tiktok.com/@donprod" 
                                            target="_blank" 
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={9}>TIKTOK</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" />     */}
                                            <div className="box-hov-indicator" 
                                                onMouseEnter={(e) => {
                                                try{
                                                    let test = document.getElementById('cursor-circle');
                                                    test.classList.add("cursor-active");
                                                    hideGhost()
                                                }
                                                catch{

                                                }
                                                }}
                                                onMouseLeave={(e) => {
                                                    try{
                                                        let test = document.getElementById('cursor-circle');
                                                        test.classList.remove("cursor-active")
                                                        showGhost()
                                                    }
                                                    catch{

                                                    }
                                                }}> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a>
                                        <a 
                                            className="contact-social"
                                            href="https://www.instagram.com/donprod" 
                                            target="_blank"   
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={10}>INSTA</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" /> */}
                                            <div className="box-hov-indicator" onMouseEnter={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.add("cursor-active");
                                                                hideGhost()
                                                            }
                                                            catch{

                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.remove("cursor-active");
                                                                showGhost()
                                                            }
                                                            catch{

                                                            }
                                                        }}> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a>
                                        <a 
                                            className="contact-social"
                                            href="mailto:doubleornothingproductions@gmail.com" 
                                            target="_blank"   
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={11}>EMAIL US</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" /> */}
                                            <div className="box-hov-indicator"
                                                onMouseEnter={(e) => {
                                                    try{
                                                        let test = document.getElementById('cursor-circle');
                                                        test.classList.add("cursor-active");
                                                        hideGhost()
                                                    }
                                                    catch{

                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    try{
                                                        let test = document.getElementById('cursor-circle');
                                                        test.classList.remove("cursor-active")
                                                        showGhost();
                                                    }
                                                    catch{

                                                    }
                                                }}
                                            > 
                                                <span className="bx-h bx-tl"></span>
                                                <span className="bx-h bx-tr"></span>
                                                <span className="bx-h bx-bl"></span>
                                                <span className="bx-h bx-br"></span>

                                                <span className="bx-v bx-tl"></span>
                                                <span className="bx-v bx-tr"></span>
                                                <span className="bx-v bx-bl"></span>
                                                <span className="bx-v bx-br"></span>
                                            </div>
                                        </a>
                                    </div>
                                </> :
                                <>
                                    <div className="stacked-statememnt__wrapper">
                                        <div className="stack-1">
                                        {
                                            "READY WHEN".split("").flat().map((char, idx) => (
                                                char == " " ? 
                                                    <div className="service_item">
                                                        <motion.span variants={defaultInfoVariants} custom={5}>&nbsp;</motion.span>
                                                    </div>
                                                : 
                                                <div className="service_item">
                                                    <motion.span variants={defaultInfoVariants} custom={5+ (idx*0.25)}>{char}</motion.span>
                                                </div>
                                            ))
                                        }
                                        </div>
                                        <div className="stack-2">
                                         {
                                            "YOU ARE.".split("").flat().map((char, idx) => (
                                                char == " " ? 
                                                    <div className="service_item">
                                                        <motion.span variants={defaultInfoVariants} custom={5}>&nbsp;</motion.span>
                                                    </div>
                                                : 
                                                <div className="service_item">
                                                    <motion.span variants={defaultInfoVariants} custom={5+ (idx*0.25)}>{char}</motion.span>
                                                </div>
                                            ))
                                        }
                                        </div>
                                        
                                    </div>
                                    <div className="mid-footer-contact-options">
                                        <a 
                                            className="contact-social"
                                            href="https://vimeo.com/donprod" 
                                            target="_blank" 
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={8}>VIMEO</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" />     */}
                                            <div className="box-hov-indicator"> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a>
                                        <a 
                                            className="contact-social"
                                            href="https://www.tiktok.com/@donprod" 
                                            target="_blank" 
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={9}>TIKTOK</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" />     */}
                                            <div className="box-hov-indicator"> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a>
                                        <a 
                                            className="contact-social"
                                            href="https://www.instagram.com/donprod" 
                                            target="_blank"   
                                        >
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={10}>VIMEO</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" /> */}
                                            <div className="box-hov-indicator"> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </a>
                                        <span className="contact-social">
                                            <div className="service_item">
                                                <motion.span variants={defaultInfoVariants} custom={11}>CONTACT@DONPROD.CO.UK</motion.span>
                                            </div>
                                            {/* <span className="underline-interact" /> */}
                                            <div className="box-hov-indicator"> 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                        </span>
                                    </div>
                                </>
                            }
                        </div>
                        <div className={`right-divide cont-mid-right ${isColour ? 'bts-colour' : 'bts-grey'}`}>
                            <div className="right-img-hider" style={{overflow: "hidden", height: "100%"}}>
                                <motion.div 
                                    className="right-img__wrapper"
                                    ref={imgWrapperRef}
                                    variants={rightImgVariants}    
                                >
                                    <div className="bts-img-start" style={{backgroundImage: 'url("../media/BTS/placeholder/BTS_0.webp")'}} />
                                </motion.div>
                            </div>
                        </div>
                    </div>
                    <div className="contact-lower__wrapper">
                        <div className="left-divide" style={{
                            padding: 0
                        }}>
                            <AnimatePresence>
                                {!remountTicker && tickerFontSize !== null &&
                                    <motion.div 
                                        className="ticker-section optimise"
                                        initial="initial"
                                        animate={isPresent ? "animate" : "exit"}
                                        exit="exit"
                                        variants={tickerSectionVariants}    
                                    >
                                        <div className="tmp-ticker__wrapper optimise"
                                            style={{
                                                fontSize: tickerFontSize+"px"
                                            }}
                                        >
                                            {
                                                Array(11).fill("").map((item, idx) => (
                                                    <div 
                                                        class="marquee enable-animation"
                                                        style={{
                                                            position: "absolute",
                                                            transform: `translateY(${-200 + (idx * 100)}%)`
                                                        }}    
                                                    >
                                                        <div className="marquee-content__wrapper">
                                                            <div class="marquee__content optimise">
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                            </div>
                                                            <div class="marquee__content optimise" aria-hidden="true">
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                                <span>DOUBLE OR NOTHING PRODUCTIONS &nbsp;</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </motion.div>
                                }
                            </AnimatePresence>
                        </div>
                        <div className="right-divide">
                            <div className="con-br__wrapper">
                                <div className="img-lower-control__wrapper">
                                    <div className="service_item">
                                        <motion.div 
                                            className="con-col-mode__wrapper"
                                            variants={standardItemVariants}
                                            custom={11}
                                        >
                                            <span>MODE: </span>
                                            <span 
                                                className="contact-upper"
                                                onClick={() => setIsColour(!isColour)}
                                                style={{
                                                    marginLeft: 5
                                                }}
                                            >
                                                {isColour ? 'COLOUR' : 'MONO'}
                                                {/* <span className="underline-interact" /> */}
                                                <div className="box-hov-indicator box-sm"
                                                    onMouseEnter={(e) => {
                                                        try{
                                                            let test = document.getElementById('cursor-circle');
                                                            test.classList.add("cursor-active");
                                                            hideGhost();
                                                        }
                                                        catch{

                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        try{
                                                            let test = document.getElementById('cursor-circle');
                                                            test.classList.remove("cursor-active");
                                                            showGhost();
                                                        }
                                                        catch{

                                                        }
                                                    }}
                                                > 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                            </span>
                                        </motion.div>
                                    </div>
                                    <div className="con-prev"> 
                                        <div className="service_item"> 
                                            <motion.span 
                                                className="contact-upper"
                                                onClick={() => handleImageCounter(-1)}
                                                variants={standardItemVariants}
                                                custom={12}    
                                            >
                                                    PREV
                                                    {/* <span className="underline-interact" /> */}
                                                    <div className="box-hov-indicator box-sm"
                                                        onMouseEnter={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.add("cursor-active");
                                                                hideGhost()
                                                            }
                                                            catch{

                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            try{
                                                                let test = document.getElementById('cursor-circle');
                                                                test.classList.remove("cursor-active")
                                                                showGhost()
                                                            }
                                                            catch{

                                                            }
                                                        }}
                                                    > 
                                                    <span className="bx-h bx-tl"></span>
                                                    <span className="bx-h bx-tr"></span>
                                                    <span className="bx-h bx-bl"></span>
                                                    <span className="bx-h bx-br"></span>

                                                    <span className="bx-v bx-tl"></span>
                                                    <span className="bx-v bx-tr"></span>
                                                    <span className="bx-v bx-bl"></span>
                                                    <span className="bx-v bx-br"></span>
                                                </div>
                                            </motion.span>
                                        </div>
                                    </div>
                                </div>
                                <div className="bts-description__wrapper">
                                    <div className="service_item" style={{
                                        textAlign: "center"
                                    }}> 
                                        <motion.span
                                            variants={standardItemVariants}
                                            custom={13}    
                                        >Isle of Wight Stand Up</motion.span>
                                    </div>
                                </div>
                                <div className="rating-wrapper">
                                    <div className="service_item"> 
                                        <motion.span
                                            onClick={() => handleRatingClick(1)}
                                            onMouseEnter={() => hideGhost()}
                                            onMouseLeave={() => showGhost()}
                                            variants={standardItemVariants}
                                            custom={14}
                                        >🔥</motion.span>
                                    </div>
                                    <div className="service_item"> 
                                        <motion.span 
                                            onClick={() => handleRatingClick(0)}
                                            onMouseEnter={() => hideGhost()}
                                            onMouseLeave={() => showGhost()}
                                            variants={standardItemVariants}
                                            custom={14}  
                                              
                                        >👎</motion.span>
                                    </div>
                                </div>
                                <div className="bts-date">
                                    <div className="service_item"> 
                                        <motion.span
                                            variants={standardItemVariants}
                                            custom={14}  
                                        >©2025</motion.span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="contact-grid__wrapper">
                    <motion.div 
                        className="c-grid-horiz-top c-horiz"
                        variants={horizGridVariants}
                        custom={0}
                    />
                    <motion.div className="c-grid-horiz-bottom c-horiz" variants={horizGridVariants} custom={1}/>
                    <motion.div className="c-grid-vert-left c-vert" variants={vertGridVariants} custom={0}/>
                    <motion.div className="c-grid-vert-right c-vert" variants={vertGridVariants} custom={1}/>
                </div>
                <div className="showreel-video__wrapper">
                    {contactData &&
                        <Showreel 
                            videoLink={contactData.showreelLink}
                            isVisible={isShowreelVisible}
                            setIsVisible={setIsShowreelVisible}
                            isShowreelVisibleRef={isShowreelVisibleRef}
                        />
                    }
                </div>
                {
                    <>
                        <div className="c-ll-cover" ref={clCoverRef}/>
                        <div className="c-rl-cover" ref={crCoverRef}/>
                    </>
                }
                <div className="manual-cacher">
                    {Array(contactData.numberStills).fill(null).map((item, idx) => (
                        <img src={`../media/BTS/placeholder/BTS_${idx}.webp`} />
                    ))}
                </div>

                <div className="mouse-tracker-wrapper" ref={mouseTrackerWrapperRef}>
                    
                    {
                        Array(numGhosts).fill(null).map((_, i) => (
                            <div 
                                className={`ghost-item optimise ghost-${i} ${isGhostActive ? 'ghost-act' : 'ghost-disact'}`}
                                ref={el => ghostRefs.current[i] = el}
                                style={{
                                    filter: `blur(${ghostBlur[i]})`,
                                    opacity: ghostOpacities[i],
                                    zIndex: ghostZs[i]
                                }}   
                            >
                                <video
                                    muted
                                    playsInline
                                    loop
                                    autoPlay
                                >
                                    <source src={'../media/trimmed_showreel.mp4'} type='video/mp4' />
                                </video>
                            </div>
                        ))
                    }
                    {
                        Array(numGhosts).fill(null).map((_, i) => (
                            <div className={`shwrel-txt shwrel-ghost-${i} ${isGhostActive ? 'ghost-act' : 'ghost-disact'}`}
                                ref={el => textGhostRefs.current[i] = el}
                                style={{
                                    filter: `blur(${ghostBlur[i]})`,
                                    opacity: ghostTextOpacities[i],
                                    zIndex: 10
                                }}
                            >
                                <motion.span>VIEW SHOWREEL</motion.span>
                            </div>
                        ))
                    }
                    
                </div>
            </motion.div>
        </>
    )
}
export default Contact;